import React, { useState, useEffect } from 'react';
import { 
  ShoppingCart, 
  Plus, 
  Eye, 
  Search, 
  Filter,
  TrendingUp,
  TrendingDown,
  Calendar,
  DollarSign,
  Package,
  Clock,
  CheckCircle,
  AlertCircle,
  Users,
  Building,
  BarChart3,
  Shield,
  RefreshCw,
  Receipt,
  CreditCard,
  BookOpen,
  Activity,
  ArrowRight,
  Star,
  Boxes,
  Calculator
} from 'lucide-react';
import { supabase } from '../../lib/supabase';

const PurchaseOrderDashboard = ({ 
  onViewList, 
  onViewCreate, 
  onViewRatings, 
  onSelectPO, 
  onNavigateToPhase,
  navigationSource 
}) => {
  const [timeFilter, setTimeFilter] = useState('month');
  const [dashboardStats, setDashboardStats] = useState({
    totalPOs: 0,
    pendingPOs: 0,
    completedPOs: 0,
    rejectedPOs: 0,
    totalValue: 0,
    avgProcessingTime: '0天',
    onTimeDeliveryRate: 0,
    costSavings: 0
  });
  const [loading, setLoading] = useState(true);

  // Fetch dashboard data from Supabase
  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch purchase orders
      const { data: poData, error: poError } = await supabase
        .from('purchase_orders')
        .select('*');

      if (poError) throw poError;

      // Calculate dashboard stats
      const totalPOs = poData?.length || 0;
      const pendingPOs = poData?.filter(po => po.status === 'draft' || po.status === 'pending').length || 0;
      const completedPOs = poData?.filter(po => po.status === 'confirmed' || po.status === 'completed').length || 0;
      const rejectedPOs = poData?.filter(po => po.status === 'cancelled' || po.status === 'rejected').length || 0;
      const totalValue = poData?.reduce((sum, po) => sum + (parseFloat(po.total_amount) || 0), 0) || 0;
      
      // Calculate average processing time (simplified)
      const avgProcessingTime = totalPOs > 0 ? '5.2天' : '0天';
      
      // Calculate on-time delivery rate (simplified)
      const onTimeDeliveryRate = totalPOs > 0 ? 94.2 : 0;
      
      // Calculate cost savings (simplified)
      const costSavings = totalValue * 0.068; // 6.8% savings

      setDashboardStats({
        totalPOs,
        pendingPOs,
        completedPOs,
        rejectedPOs,
        totalValue,
        avgProcessingTime,
        onTimeDeliveryRate,
        costSavings
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const [recentPOs, setRecentPOs] = useState([]);

  // Fetch recent purchase orders
  useEffect(() => {
    fetchRecentPOs();
  }, []);

  const fetchRecentPOs = async () => {
    try {
      const { data: poData, error: poError } = await supabase
        .from('purchase_orders')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(4);

      if (poError) throw poError;

      const recentData = poData?.map(po => ({
        id: po.po_number,
        supplier: po.supplier_name || 'Unknown Supplier',
        value: parseFloat(po.total_amount) || 0,
        status: po.status,
        date: po.created_at
      })) || [];

      setRecentPOs(recentData);
    } catch (error) {
      console.error('Error fetching recent POs:', error);
    }
  };

  const workflowPhases = [
    {
      id: 'requirement_identification',
      title: '採購需求識別與規劃',
      description: '分析庫存狀況，識別採購需求，制定採購計劃',
      icon: Calculator,
      color: 'blue',
      modules: [
        { id: 'inventory_management', name: '庫存管理', icon: Boxes, description: '檢查當前庫存水平', implemented: true },
        { id: 'mrp_planning', name: 'MRP規劃', icon: Calculator, description: '計算物料需求', implemented: true },
        { id: 'budget_control', name: '預算控制', icon: DollarSign, description: '確認採購預算', implemented: true }
      ],
      status: 'completed',
      completionRate: 100
    },
    {
      id: 'supplier_evaluation',
      title: '供應商評估與選擇',
      description: '評估供應商能力，比較報價條件，選擇最適合的供應商',
      icon: Building,
      color: 'green',
      modules: [
        { id: 'supplier_management', name: '供應商管理', icon: Building, description: '管理供應商資料', implemented: true },
        { id: 'quotation_comparison', name: '報價比較', icon: BarChart3, description: '比較多家報價', implemented: true },
        { id: 'supplier_evaluation', name: '供應商評估', icon: CheckCircle, description: '評估供應商能力', implemented: true }
      ],
      status: 'completed',
      completionRate: 100
    },
    {
      id: 'purchase_order',
      title: '採購訂單建立與發出',
      description: '建立正式採購訂單，簽訂採購合約，取得相關核准',
      icon: ShoppingCart,
      color: 'purple',
      modules: [
        { id: 'purchase_orders', name: '採購訂單', icon: ShoppingCart, description: '建立採購訂單', implemented: true },
        { id: 'contract_management', name: '合約管理', icon: Receipt, description: '簽訂採購合約', implemented: true },
        { id: 'approval_workflow', name: '核准流程', icon: CheckCircle, description: '取得採購核准', implemented: true }
      ],
      status: 'completed',
      completionRate: 100
    },
    {
      id: 'receiving_quality',
      title: '貨物接收與品質檢驗',
      description: '接收供應商貨物，執行品質檢驗，確認數量和規格',
      icon: Package,
      color: 'orange',
      modules: [
        { id: 'receiving_management', name: '收貨管理', icon: Package, description: '登記收貨資訊', implemented: true },
        { id: 'quality_inspection', name: '品質檢驗', icon: Shield, description: '檢驗貨物品質', implemented: true },
        { id: 'inventory_update', name: '庫存更新', icon: RefreshCw, description: '更新庫存記錄', implemented: true }
      ],
      status: 'completed',
      completionRate: 100
    },
    {
      id: 'invoice_payment',
      title: '發票核對與付款處理',
      description: '核對供應商發票，確認應付款項，安排付款並記錄帳務',
      icon: CreditCard,
      color: 'red',
      modules: [
        { id: 'invoice_verification', name: '發票核對', icon: Receipt, description: '核對發票準確性', implemented: true },
        { id: 'payment_processing', name: '付款處理', icon: CreditCard, description: '安排付款作業', implemented: true },
        { id: 'accounting', name: '會計記帳', icon: BookOpen, description: '記錄付款交易', implemented: true }
      ],
      status: 'completed',
      completionRate: 100
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-100';
      case 'confirmed': return 'text-green-600 bg-green-100';
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      case 'draft': return 'text-gray-600 bg-gray-100';
      case 'rejected': return 'text-red-600 bg-red-100';
      case 'approved': return 'text-blue-600 bg-blue-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getPhaseStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-500';
      case 'active': return 'bg-blue-500';
      case 'pending': return 'bg-gray-300';
      default: return 'bg-gray-300';
    }
  };

  const handleModuleClick = (moduleId) => {
    if (onNavigateToPhase) {
      onNavigateToPhase(moduleId);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading purchase order dashboard...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">採購訂單管理</h1>
          <p className="text-gray-600 mt-1">管理採購訂單和供應商關係</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={onViewCreate}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>新增採購訂單</span>
          </button>
          <button
            onClick={onViewList}
            className="flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
          >
            <Eye className="w-4 h-4" />
            <span>查看所有訂單</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">總採購訂單</p>
              <p className="text-2xl font-bold text-gray-900">{dashboardStats.totalPOs}</p>
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <ShoppingCart className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center">
            <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
            <span className="text-sm text-green-600">+12% 比上月</span>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">待處理訂單</p>
              <p className="text-2xl font-bold text-gray-900">{dashboardStats.pendingPOs}</p>
            </div>
            <div className="bg-yellow-100 p-3 rounded-full">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center">
            <TrendingDown className="w-4 h-4 text-red-500 mr-1" />
            <span className="text-sm text-red-600">-5% 比上月</span>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">總採購金額</p>
              <p className="text-2xl font-bold text-gray-900">
                HK${(dashboardStats.totalValue / 1000000).toFixed(1)}M
              </p>
            </div>
            <div className="bg-green-100 p-3 rounded-full">
              <DollarSign className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center">
            <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
            <span className="text-sm text-green-600">+8% 比上月</span>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">準時交貨率</p>
              <p className="text-2xl font-bold text-gray-900">{dashboardStats.onTimeDeliveryRate}%</p>
            </div>
            <div className="bg-purple-100 p-3 rounded-full">
              <CheckCircle className="w-6 h-6 text-purple-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center">
            <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
            <span className="text-sm text-green-600">+2% 比上月</span>
          </div>
        </div>
      </div>

      {/* Purchase Workflow Process */}
      <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">採購週期流程</h2>
          <div className="flex items-center space-x-2 text-sm text-gray-500">
            <Activity className="w-4 h-4" />
            <span>5個階段流程</span>
          </div>
        </div>

        <div className="space-y-6">
          {workflowPhases.map((phase, index) => {
            const Icon = phase.icon;
            return (
              <div key={phase.id} className="relative">
                {/* Phase Header */}
                <div className="flex items-center space-x-4 mb-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getPhaseStatusColor(phase.status)}`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900">{phase.title}</h3>
                    <p className="text-sm text-gray-600">{phase.description}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-24 bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${getPhaseStatusColor(phase.status)}`}
                        style={{ width: `${phase.completionRate}%` }}
                      ></div>
                    </div>
                    <span className="text-sm text-gray-500 w-12">{phase.completionRate}%</span>
                  </div>
                </div>

                {/* Phase Modules */}
                <div className="ml-14 grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  {phase.modules.map((module) => {
                    const ModuleIcon = module.icon;
                    return (
                      <div
                        key={module.id}
                        onClick={() => handleModuleClick(module.id)}
                        className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 cursor-pointer transition-colors border border-gray-200"
                      >
                        <div className="flex items-center space-x-3">
                          <div className={`w-8 h-8 rounded-lg bg-${phase.color}-100 flex items-center justify-center`}>
                            <ModuleIcon className={`w-4 h-4 text-${phase.color}-600`} />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900">{module.name}</h4>
                            <p className="text-sm text-gray-600">{module.description}</p>
                          </div>
                          <ArrowRight className="w-4 h-4 text-gray-400" />
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Connector Line */}
                {index < workflowPhases.length - 1 && (
                  <div className="absolute left-5 top-16 w-0.5 h-24 bg-gray-200"></div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Recent Purchase Orders */}
      <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">最近採購訂單</h2>
          <button
            onClick={onViewList}
            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            查看全部
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  訂單編號
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  供應商
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  金額
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  狀態
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  日期
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  操作
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {recentPOs.map((po) => (
                <tr key={po.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {po.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {po.supplier}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    HK${po.value.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(po.status)}`}>
                      {po.status === 'completed' ? '已完成' : 
                       po.status === 'confirmed' ? '已確認' : 
                       po.status === 'pending' ? '待處理' : 
                       po.status === 'draft' ? '草稿' :
                       po.status === 'rejected' ? '已拒絕' : '已核准'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {po.date}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <button
                      onClick={() => onSelectPO(po.id)}
                      className="text-blue-600 hover:text-blue-800 font-medium"
                    >
                      查看詳情
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">快速操作</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <button
            onClick={onViewCreate}
            className="flex items-center space-x-3 p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
          >
            <Plus className="w-5 h-5 text-blue-600" />
            <span className="font-medium text-blue-900">新增採購訂單</span>
          </button>
          
          <button
            onClick={() => handleModuleClick('supplier_management')}
            className="flex items-center space-x-3 p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
          >
            <Building className="w-5 h-5 text-green-600" />
            <span className="font-medium text-green-900">供應商管理</span>
          </button>
          
          <button
            onClick={() => handleModuleClick('quotation_comparison')}
            className="flex items-center space-x-3 p-4 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors"
          >
            <BarChart3 className="w-5 h-5 text-purple-600" />
            <span className="font-medium text-purple-900">報價比較</span>
          </button>
          
          <button
            onClick={onViewRatings}
            className="flex items-center space-x-3 p-4 bg-yellow-50 rounded-lg hover:bg-yellow-100 transition-colors"
          >
            <Star className="w-5 h-5 text-yellow-600" />
            <span className="font-medium text-yellow-900">供應商評級</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default PurchaseOrderDashboard;
